import { useAgent } from '../store/agent.store'

const EXPIRY_MARGIN_SECONDS = 60

const tokenExpiry = (token: string): number | null => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1] ?? ''))
    return typeof payload?.exp === 'number' ? payload.exp : null
  } catch {
    return null
  }
}

/**
 * Swaps in a fresh JWT before it expires. Mirrors the admin panel's refreshAuth
 * (admin/src/authProvider.ts) because the backend and the 3600s TTL are the
 * same.
 */
const ensureFreshToken = async (): Promise<string> => {
  const { accessToken, refreshToken, setTokens } = useAgent.getState()
  if (!accessToken || !refreshToken) {
    return accessToken
  }

  const exp = tokenExpiry(accessToken)
  if (exp !== null && exp - Date.now() / 1000 > EXPIRY_MARGIN_SECONDS) {
    return accessToken
  }

  const response = await fetch(`${process.env.REACT_APP_API}/api/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh_token: refreshToken }),
  })
  const data = await response.json().catch(() => ({}))

  if (!data?.token) {
    return accessToken
  }

  setTokens(data.token, data.refresh_token ?? refreshToken)
  return data.token
}

/**
 * Like ApiInterceptor, but sends the agent's Bearer token. Only the
 * /api/agent/* endpoints need this — every /client/* call stays on the plain
 * interceptor, which sends no Authorization header at all.
 */
export const ApiInterceptorAuthed = async <T>(url: string, options?: RequestInit): Promise<T> => {
  const token = await ensureFreshToken()
  const opts: RequestInit = options ?? {}

  opts.headers = {
    ...opts.headers,
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }

  const response = await fetch(`${process.env.REACT_APP_API}${url}`, opts)
  const text = await response.text()

  if (!text) {
    return {} as T
  }

  return JSON.parse(text) as T
}
